import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './VNMenuItem.module.scss'
import { ButtonBase } from '@material-ui/core'
import useCartItem from '../hooks/useCartItem'
import VNMenuQuantity from './VNMenuQuantity'
import classNames from 'classnames'

/**
 * MenuItem component
 */
const VNMenuItem = ({
  cartIndex = -1,
  cartQuantity = 0,
  cartQuantityWithModifiers = 0,
  color,
  hasRequiredModifiers = false,
  id: itemId,
  isAlcohol,
  max,
  min,
  name,
  price,
}) => {
  const throttlingRef = useRef(null)
  const throttlingActiveRef = useRef(false) // checks if in middle of throttle timer
  const isInCartRef = useRef(cartQuantity > 0)
  const latestQuantityRef = useRef(cartQuantity)
  const [localQuantity, setLocalQuantity] = useState(cartQuantity)
  const opacity = useMemo(() => (cartQuantity === max ? 0.6 : 1), [cartQuantity, max])
  const unavailable = useMemo(() => price === 'SOLD OUT', [price])
  const localStyle = color ? { backgroundColor: color, opacity } : null
  const { addItemWithModifiers, upsertItem } = useCartItem({
    throttlingRef,
    throttlingActiveRef,
    isInCartRef,
    latestQuantityRef,
  })

  // resets to cart quantity when cart quantity changes outside of component
  useEffect(() => {
    if (throttlingActiveRef.current) return
    if (cartQuantity === localQuantity) return

    isInCartRef.current = cartQuantity > 0
    latestQuantityRef.current = cartQuantity
    setLocalQuantity(cartQuantity)
  }, [cartQuantity, localQuantity])

  /**
   * Update local quantity when tapping on item
   */
  const handleUpdateQuantities = useCallback(() => {
    if (localQuantity === 0) {
      setLocalQuantity(min || 1)
      latestQuantityRef.current = min || 1
      return
    }

    setLocalQuantity((prevQuantity) => prevQuantity + 1)
    latestQuantityRef.current++
  }, [localQuantity, min])

  /**
   * Add item to cart with throttling functionality
   */
  const handleAddToCart = useCallback(() => {
    if (localQuantity === max) return

    // Update local quantity immediately
    handleUpdateQuantities()

    if (hasRequiredModifiers) {
      addItemWithModifiers({ itemId, isAlcohol })
      return
    }

    upsertItem({
      itemId,
      isAlcohol,
      hasRequiredModifiers,
      max,
      cartIndex,
    })
  }, [
    localQuantity,
    max,
    handleUpdateQuantities,
    hasRequiredModifiers,
    upsertItem,
    itemId,
    isAlcohol,
    cartIndex,
    addItemWithModifiers,
  ])

  return (
    <ButtonBase
      component="div"
      className={classNames(styles.container, { [styles.unavailable]: unavailable })}
      style={localStyle}
      onClick={handleAddToCart}
    >
      <div className={styles.title}>
        <span className={styles.name}>{name}</span>
      </div>
      <div className={styles.details}>
        <div className={styles.inventoryPrice}>
          <span className={styles.price}>{price}</span>
        </div>
        <VNMenuQuantity color={color} value={localQuantity + cartQuantityWithModifiers} />
      </div>
    </ButtonBase>
  )
}

export default memo(VNMenuItem)
